import { useEffect, useRef, useState } from "react";
import { PlayerProps } from "./player.props";
import { ProgramType } from "src/enums";
import { PlayerBuyPackageButton } from "./player.buy.package.button";
import { useDispatch } from "react-redux";
import { ProgramViewAction } from "src/store";
import { useParams } from "react-router";
import { Player as PlayerFunctions, PlayerService } from "src/services";
import LazyLoad from "vanilla-lazyload";
import { PlayerTraffic } from "./player.traffic";
import { Button } from "react-bootstrap";
import { PlayerFullScreen } from "./player.fullscreen";

export const Player: React.FC<PlayerProps> = ({
  message,
  poster,
  programType,
  packageId,
  logo,
  trafficUsed,
  type,
  url,
  vastUrl,
  streams,
  currentStream,
  qualityConfig,
  programTitle,
}) => {
  const { id }: any = useParams();

  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const player = useRef<PlayerFunctions | null>(null);
  const dispatch = useDispatch();

  const [startTime, setStartTime] = useState<null | Date>(null);

  useEffect(() => {
    const initPlayer = async () => {
      setStartTime(new Date());
      if (videoRef && videoRef.current) {
        const onPlay = () => {
          if (currentStream)
            dispatch(ProgramViewAction.startReporting(currentStream.id));
        };

        const onPause = () => {
          dispatch(ProgramViewAction.stopReporting());
        };

        if (player.current) {
          player.current.changeStream(
            url,
            type,
            vastUrl,
            poster,
            currentStream,
            onPlay,
            onPause
          );
        } else {
          player.current = await PlayerService.makePlayer();

          player.current.initPlayer(
            videoRef.current,
            url,
            type,
            poster,
            vastUrl,
            streams,
            currentStream,
            onPlay,
            onPause,
            undefined,
            undefined,
            true,
            !qualityConfig?.hqEnabled,
            qualityConfig?.visible
          );
        }
      }
    };

    const updateImages = () => {
      const lazyload = new LazyLoad({
        elements_selector: ".lazy",
        callback_error: (element: HTMLElement) => {
          const placeholder = element.getAttribute("data-placeholder");
          if (placeholder) element.setAttribute("src", placeholder);
        },
      });

      lazyload.update();
    };

    initPlayer();
    if (trafficUsed) updateImages();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, vastUrl]);

  useEffect(() => {
    return () => {
      //no need to call stopReporting, but better safe than sorry
      const onDispose = () => dispatch(ProgramViewAction.stopReporting());
      if (player.current) {
        player.current.exitPictureInPicture();
        player.current.disposePlayer(onDispose);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasLockSub =
    currentStream &&
    currentStream.subs &&
    currentStream.subs.some((x) => x.lock);
  const hasLockStram =
    streams && streams.some((x) => x.subs && x.subs.every((y) => y.lock));

  const showBuyButton = hasLockStram || hasLockSub;

  return (
    <>
      <div
        ref={wrapperRef}
        className={`video-js-box vjs-player fkplayer vjs-player-wrapper${
          programType === ProgramType.ArchiveVideo ? " archive-player" : ""
        }`}
      >
        <div data-vjs-player>
          <video
            poster={poster}
            ref={videoRef}
            className="video-js"
            playsInline
          ></video>
          {player.current && (
            <div className="selectableRow">
              <PlayerBuyPackageButton
                player={player.current}
                hasLockStram={hasLockStram}
                hasLockSub={hasLockSub}
                packageId={packageId}
                programId={id}
                programTitle={programTitle}
              />
              <PlayerFullScreen
                player={player.current}
                hasLockStram={hasLockStram}
                hasLockSub={hasLockSub}
                packageId={packageId}
                programId={id}
              />
            </div>
          )}

          <PlayerTraffic
            startTime={startTime}
            trafficUsed={trafficUsed}
            logo={logo}
            message={message}
          />
        </div>
      </div>
    </>
  );
};
